import { ref } from 'vue';
import { sendGet } from '@/Utils/api';
import { MailDeliverability } from '@/models/deliveryOrder';
import type { LocationDataType, CustomLocationType } from '@/models/address';
import { addressService } from '@/services/address';
import { US_ALIASES, usStateList } from '@/constants/address';

const registrationStartAddress = ref<CustomLocationType | any>(null);
const registrationEndAddress = ref<CustomLocationType | any>(null);

export const useAddress = () => {
  const checkForAddressDeliverability = async (address: CustomLocationType, options?: { validateAU?: boolean }) => {
    // ENG-9406: AU validation is opt-in per caller. Only mailing flows set
    // validateAU — registration and other flows keep the previous behavior of
    // skipping Lob validation for AU addresses so Lob strictness can't block them.
    if (address.country === 'AU' && !options?.validateAU) {
      return;
    }

    const response = await addressService.checkLobAddress({
      streetAddress: address.streetAddress,
      streetAddress2: address.streetAddress2,
      city: address.city,
      zip: address.zip,
      state: address.state,
      country: address.country,
    });

    // if address is unavailable to delivery
    if (response.deliverability !== MailDeliverability.Deliverable) {
      throw new Error(response.message ?? 'There is an issue checking deliverability of address');
    }
  };

  // convert address object into string
  /*
    {
      streetAddress: '1435 Garfield Avenue',
      city: 'Pasadena',
      state: 'CA',
      zip: '91104',
      country: 'US'
    } => `1435 Garfield Avenue, Pasadena, CA 91104, US`
  */
  const getFormattedAddress = (address: {
    streetAddress: string;
    streetAddress2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  }) => {
    const streetAddressLine = [address.streetAddress, address.streetAddress2].filter(Boolean).join(' ');
    const stateZip = [address.state, address.zip].filter(Boolean).join(' ');

    return [streetAddressLine, address.city, stateZip, address.country].filter(Boolean).join(', ');
  };

  /*
    get address object from string address
    1435 Garfield Avenue, Pasadena, CA 91104, US =>
    {
      streetAddress: '1435 Garfield Avenue',
      city: 'Pasadena',
      state: 'CA',
      zip: '91104',
      country: 'US'
    }
  */
  const getAddressObjectFromString = (addy: string) => {
    const addyArr = addy.split(', ');
    let stateZip: string[] = [];
    if (addyArr[2]) {
      stateZip = addyArr[2].split(' ');
    } else {
      stateZip[0] = '';
      stateZip[1] = '';
    }

    const streetAddress = addyArr[0];
    const city = addyArr[1];
    const state = stateZip[0];
    const zip = stateZip[1];
    const country = addyArr[3] || 'US';

    return {
      streetAddress: streetAddress,
      city: city,
      state: state,
      zip: zip,
      country: country,
    } as LocationDataType;
  };

  // parse address from auto complete address result
  const parseAddressComponentsToAddress = (address_components?: google.maps.GeocoderAddressComponent[]) => {
    const addressObjectKey: any = {
      street_number: 'short_name',
      route: 'long_name',
      bus_station: 'long_name',
      transit_station: 'long_name',
      locality: 'long_name',
      administrative_area_level_1: 'short_name',
      administrative_area_level_2: 'short_name',
      country: 'short_name',
      postal_code: 'short_name',
      intersection: 'long_name',
    };

    const addressObject: any = {
      street_number: null,
      route: null,
      locality: null,
      administrative_area_level_1: null,
      administrative_area_level_2: null,
      country: null,
      postal_code: null,
      bus_station: null,
      transit_station: null,
      intersection: null,
    };

    let streetAddress = '';

    if (address_components) {
      for (let i = 0; i < address_components.length; i++) {
        const addressType = address_components[i].types[0];
        if (addressObjectKey[addressType]) {
          const addressKey = addressObjectKey[addressType] as 'short_name' | 'long_name';
          addressObject[addressType] = address_components[i][addressKey];
        }
      }

      streetAddress = `${addressObject.street_number ? addressObject.street_number + ' ' : ''}${
        addressObject.route ? addressObject.route : ''
      }`;
      streetAddress = streetAddress || addressObject.bus_station || addressObject.transit_station;
    }

    return {
      streetAddress: streetAddress,
      streetNumber: addressObject.street_number,
      route: addressObject.route,
      city: addressObject.locality || addressObject.administrative_area_level_2,
      state: addressObject.administrative_area_level_1,
      zip: addressObject.postal_code,
      country: addressObject.country,
      intersection: addressObject.intersection ? addressObject.intersection.split('&') : [],
      intersectionAddress: addressObject.intersection,
    } as LocationDataType;
  };

  // get street address regarding with address type - intersection or normal
  const getStreetAddressByType = (data: CustomLocationType) => {
    let streetAddress: string;

    if (data.type) {
      // the address is intersection address
      streetAddress = `${data.streetAddress} & ${data.streetAddress1}`;
    } else {
      // the address is normal
      streetAddress = data.streetAddress;
    }

    return streetAddress;
  };

  // get distrance from two coordinates
  const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    // const R = 6371000; // earth's radius (meters)
    const R = 3959; // earth's radius (miles)
    const φ1 = lat1 * (Math.PI / 180);
    const φ2 = lat2 * (Math.PI / 180);
    // tslint:disable-next-line:variable-name
    const Δφ = (lat2 - lat1) * (Math.PI / 180);
    // tslint:disable-next-line:variable-name
    const Δλ = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;

    return Number(d.toFixed(2)); // units are in miles
  };

  // check the distrance of two coordinates with defined distance and compare operator
  const compareDistances = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
    targetDistanceMiles: number = 0.01,
    compareOperator: '<' | '<=' | '>' | '>=' | '=' = '<=',
  ) => {
    const distance = getDistance(lat1, lon1, lat2, lon2);

    if (compareOperator === '<') {
      return distance < targetDistanceMiles;
    } else if (compareOperator === '<=') {
      return distance <= targetDistanceMiles;
    } else if (compareOperator === '>') {
      return distance > targetDistanceMiles;
    } else if (compareOperator === '>=') {
      return distance >= targetDistanceMiles;
    } else if (compareOperator === '=') {
      return distance === targetDistanceMiles;
    }

    return false;
  };

  // trim and remove period from string
  const trimAndRemovePeriod = (str: string, isNeedToKeepEndPeriod: boolean) => {
    if (!str) {
      return str;
    }

    let updatedStr = str.trim();

    // if the string has '.' at the end, remove it
    if (!isNeedToKeepEndPeriod && updatedStr.substr(-1) === '.') {
      updatedStr = updatedStr.substr(0, updatedStr.length - 1);
    }

    return updatedStr;
  };

  // remove trim and dot of address
  const trimAddress = (address: any) => {
    const fields = [
      // General address fields
      'name',
      'streetAddress',
      'streetAddress1',
      'streetAddress2',
      'city',
      'state',
      'zip',
      'country',
      // Mailing address fields
      'mailingStreetAddress',
      'mailingStreetAddress2',
      'mailingCity',
      'mailingState',
      'mailingZip',
      'mailingCountry',
      // Billing address fields
      'address_line1',
      'address_line2',
      'address_city',
      'address_state',
      'address_zip',
      'address_country',
    ];

    const fieldsToKeepEndPeriod = ['streetAddress', 'mailingStreetAddress', 'address_line1'];

    for (const field of fields) {
      if (!address[field]) {
        continue;
      }

      address[field] = trimAndRemovePeriod(address[field], fieldsToKeepEndPeriod.includes(field));
    }

    return address;
  };

  const updateAddressWithGeocode = (address: CustomLocationType, lat: number | string | null, lng: number | string | null) => {
    address.latitude = lat ? Number(lat) : null;
    address.longitude = lng ? Number(lng) : null;

    return address;
  };

  const makeAddressObject = (address: any) => {
    const newAddress = {
      id: address.id, // only custom locations *1-*8 has an id because they are in a separate table callerAddresses
      name: address.name,
      streetAddress: address.streetAddress || '',
      streetAddress1: '',
      streetAddress2: address.streetAddress2 || '',
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country,
      latitude: address.latitude ? Number(address.latitude) : null,
      longitude: address.longitude ? Number(address.longitude) : null,
      googlePlaceId: address?.googlePlaceId,
    };

    return newAddress;
  };

  const getAnchorBound = (
    address: {
      latitude?: number | null;
      longitude?: number | null;
    } | null,
  ) => {
    if (!window.google) {
      return null;
    }

    if (!address?.latitude || !address?.longitude) {
      return null;
    }

    const circle = new google.maps.Circle({
      center: {
        lat: address.latitude,
        lng: address.longitude,
      },
      radius: 100,
    });

    return circle.getBounds();
  };

  const getWeather = async (lat: number, lng: number) => {
    try {
      const response = await sendGet('getWeather', {
        lat,
        lng,
      });

      const data = response.data;
      const temperatureF = Number(((data.main.temp - 273.15) * 9) / 5 + 32).toFixed(1);
      const temperatureC = ((Number(temperatureF) - 32) * 0.55).toFixed(1);
      const weatherText = `${data.weather[0].main} ${temperatureF}°F (${temperatureC}°C)`;

      return {
        code: data.weather[0].icon,
        text: weatherText,
      };
    } catch (error) {
      return null;
    }
  };

  const setRegistrationStartAddress = (formData: CustomLocationType | null) => {
    registrationStartAddress.value = { ...(formData || {}) };
  };

  const setRegistrationEndAddress = (formData: CustomLocationType | null) => {
    registrationEndAddress.value = { ...(formData || {}) };
  };

  const checkOutsideUSAddress = (address: CustomLocationType | null) => {
    if (!address) {
      return false;
    }

    const country = address.country?.trim()?.toLowerCase() ?? '';
    const state = address.state?.trim()?.toLowerCase() ?? '';
    if (!country && !state) {
      return false;
    }

    if (country) {
      const isUSCountry = US_ALIASES.some((item) => item.trim().toLocaleLowerCase() === country);

      if (isUSCountry) {
        return false;
      }
    }

    if (state) {
      const isUSState = usStateList.some((item) => item.abbreviation.toLowerCase() === state || item.name.toLowerCase() === state);

      if (isUSState) {
        return false;
      }
    }

    return true;
  };

  return {
    registrationStartAddress,
    registrationEndAddress,
    checkForAddressDeliverability,
    getFormattedAddress,
    getAddressObjectFromString,
    parseAddressComponentsToAddress,
    getStreetAddressByType,
    compareDistances,
    trimAddress,
    updateAddressWithGeocode,
    makeAddressObject,
    getDistance,
    getAnchorBound,
    getWeather,
    setRegistrationStartAddress,
    setRegistrationEndAddress,
    checkOutsideUSAddress,
  };
};
